import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { IconPrimitive } from '#pplx/frontend/aether/primitives/IconPrimitive/index.js';
import { PortalPrimitive } from '#pplx/frontend/aether/primitives/PortalPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { useDropZone } from '#pplx/frontend/components/hooks/useDropZone.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
export const DropZone = React.memo(function DropZone({ onDrop, disabled = false, children, dropZoneClassName, dropLabel, invalidDropLabel, dropIcon = $icon('arrow-bar-to-down'), dropBehavior = 'file-element-only', hideContent = false, validateDragItems, overlayFrameRef, scope, ...divProps }) {
    const { $t } = useIntl();
    const { isDragging, isDraggingFile, isOver, hasInvalidFiles, isViewportDropTarget, rootProps } = useDropZone({
        onDrop,
        disabled,
        dropBehavior,
        validateDragItems,
        scope,
    });
    // Only the zone that receives viewport drops draws the full-screen overlay;
    // other mounted zones fall back to their element overlay.
    const isViewportOverlay = isViewportDropTarget &&
        isDragging &&
        (dropBehavior === 'all-viewport-wide' ||
            (isDraggingFile && dropBehavior === 'file-viewport-wide'));
    const acceptsDraggingPayload = isDraggingFile || dropBehavior !== 'file-element-only';
    const finalDropLabel = dropLabel ??
        (isViewportOverlay && isDraggingFile
            ? $t({
                defaultMessage: 'Drop files anywhere to add them.',
                id: '250b5d10fb',
                description: 'Instruction shown over the viewport while files are being dragged',
            })
            : isViewportOverlay
                ? $t({
                    defaultMessage: 'Drop text anywhere to add it.',
                    id: '4160d74ba0',
                    description: 'Instruction shown over the viewport while text is being dragged',
                })
                : isDraggingFile
                    ? $t({
                        defaultMessage: 'Drop your files here.',
                        id: 'dab2d1313c',
                        description: 'Text in the Drop Zone form input section',
                    })
                    : $t({
                        defaultMessage: 'Drop text here.',
                        id: 'de1f28b4d5',
                        description: 'Instruction shown over an input while text is being dragged',
                    }));
    const finalInvalidDropLabel = invalidDropLabel ??
        $t({
            defaultMessage: 'Unsupported file type.',
            id: '8ada6144b8',
            description: 'Text shown in the drop zone when dragging unsupported file types',
        });
    const rootRef = React.useRef(null);
    const [elementOverlayStyle, setElementOverlayStyle] = React.useState();
    React.useLayoutEffect(() => {
        const frame = isDragging && !isViewportOverlay ? overlayFrameRef?.current : null;
        const root = rootRef.current;
        const view = frame?.ownerDocument.defaultView;
        if (!frame || !root || !view) {
            setElementOverlayStyle(undefined);
            return;
        }
        const frameRect = frame.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        setElementOverlayStyle({
            borderRadius: view.getComputedStyle(frame).borderRadius,
            top: frameRect.top - rootRect.top,
            right: rootRect.right - frameRect.right,
            bottom: rootRect.bottom - frameRect.bottom,
            left: frameRect.left - rootRect.left,
        });
    }, [isDragging, isViewportOverlay, overlayFrameRef]);
    const isActiveDropTarget = isViewportOverlay || isOver;
    const showInvalid = isDraggingFile && isActiveDropTarget && hasInvalidFiles;
    const overlay = isDragging && acceptsDraggingPayload ? (_jsxs(_Fragment, { children: [_jsx(BoxPrimitive, { rounded: isViewportOverlay ? undefined : '2xl', bg: "background", className: classNames('pointer-events-none opacity-80', isViewportOverlay ? 'fixed inset-0 z-[999]' : 'absolute inset-0 z-20'), style: elementOverlayStyle }), _jsx(BoxPrimitive, { role: "status", display: "flex", align: "center", justify: "center", className: classNames('pointer-events-none gap-x-2 animate-in fade-in rounded-2xl border-2 border-dashed transition-all duration-200', isViewportOverlay ? 'fixed inset-2 z-[999]' : 'absolute inset-0 z-20', {
                    '!border-super-strong': isActiveDropTarget && !hasInvalidFiles,
                    '!border-negative-strong': showInvalid,
                }, dropZoneClassName?.(isDraggingFile, isOver)), style: elementOverlayStyle, children: !hideContent && (_jsxs(TextPrimitive, { color: showInvalid ? 'caution' : 'super', children: [_jsx(IconPrimitive, { icon: dropIcon, className: "mr-2" }), showInvalid ? finalInvalidDropLabel : finalDropLabel] })) })] })) : null;
    return (_jsxs("div", { ref: rootRef, className: "relative", ...rootProps, ...divProps, children: [isDragging && acceptsDraggingPayload && disabled ? (_jsx(BoxPrimitive, { display: "flex", align: "center", justify: "center", className: classNames('bg-subtle absolute inset-0 z-20 rounded-2xl border-2 border-dashed', { '!border-negative-strong': isOver }, dropZoneClassName?.(isDraggingFile, isOver)), children: _jsxs(TextPrimitive, { color: "caution", children: [_jsx(IconPrimitive, { icon: dropIcon, className: "mr-2" }), _jsx(FormattedMessage, { defaultMessage: "Drop zone disabled", id: "8f2e93b9ef", description: "Text shown when files are dragged over a disabled drop zone in the Drop Zone form input section" })] }) })) : isViewportOverlay ? (_jsx(PortalPrimitive, { children: overlay })) : (overlay), children] }));
});
//# sourceMappingURL=DropZone.js.map